// ABOUTME: The colour theme — the viewer's choice (system / light / dark), kept on this device, and the
// ABOUTME: `dark` class on <html> that theme.css reads. index.html applies the same choice before the first frame.

import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { storageService } from "@shared/services/storageService"

export const THEME_PREFERENCES = ["system", "light", "dark"] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]
export type ResolvedTheme = "light" | "dark"

export interface ThemeState {
  preference: ThemePreference
  resolved: ResolvedTheme
}

/**
 * A device that never chose follows the OS (docs/DARK_THEME_SPEC.md §3.3). The boot script in index.html
 * falls back to the same mode.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system"

/** The browser's chrome takes the colour of the top of the page — the card. index.html repeats the dark one. */
export const THEME_COLOR: Record<ResolvedTheme, string> = { light: "#ffffff", dark: "#17181a" }

const DARK_QUERY = "(prefers-color-scheme: dark)"

const isThemePreference = (value: unknown): value is ThemePreference =>
  THEME_PREFERENCES.includes(value as ThemePreference)

export function readThemePreference(): ThemePreference {
  const stored = storageService.getString(STORAGE_KEYS.THEME)
  return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light"
  return preference
}

const darkQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === "function" ? window.matchMedia(DARK_QUERY) : null

const systemPrefersDark = (): boolean => darkQuery()?.matches ?? false

/** The color-scheme meta, which paints the canvas before any CSS — kept in step with index.html's script. */
export const COLOR_SCHEME_META: Record<ResolvedTheme, string> = { light: "light only", dark: "dark" }

/** Pages on screen that hold the light theme (holdLightTheme) — while any does, the page is drawn light. */
let lightHolds = 0

function applyTheme(resolved: ResolvedTheme): void {
  const shown = lightHolds > 0 ? "light" : resolved
  document.documentElement.classList.toggle("dark", shown === "dark")
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[shown])
  document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", COLOR_SCHEME_META[shown])
}

const listeners = new Set<() => void>()
let state: ThemeState | null = null

/** One object per distinct state: useSyncExternalStore re-renders on identity, not on value. */
export function getThemeSnapshot(): ThemeState {
  if (!state) {
    const preference = readThemePreference()
    state = { preference, resolved: resolveTheme(preference, systemPrefersDark()) }
  }
  return state
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The preference lives in memory as well as in storage: a write that storage refuses (private mode,
 * full quota) must still switch the theme for this session.
 */
function update(preference: ThemePreference): void {
  const resolved = resolveTheme(preference, systemPrefersDark())
  applyTheme(resolved)
  const current = getThemeSnapshot()
  if (current.preference === preference && current.resolved === resolved) return
  state = { preference, resolved }
  listeners.forEach((listener) => listener())
}

/**
 * Draws the page light while a screen made for one look is shown — the Pro landing, whose photos and fixed
 * black sections are its design in either theme. The viewer's choice stays as it is, and a switch made
 * meanwhile waits for the release. Returns the release, so it fits a `useEffect`; each release counts once.
 */
export function holdLightTheme(): () => void {
  lightHolds += 1
  applyTheme(getThemeSnapshot().resolved)
  let released = false
  return () => {
    if (released) return
    released = true
    lightHolds -= 1
    applyTheme(getThemeSnapshot().resolved)
  }
}

/** On <html> while a picture copies the page: theme.css holds every transition for that moment. */
const CAPTURE_CLASS = "theme-capture"

/**
 * Runs `capture` — an html2canvas call — on a light page, so a picture that leaves the app (a share card,
 * an exported or printed report) is light whatever the screen shows. html2canvas copies the page inside
 * the call, before its first await (node_modules/html2canvas-pro/dist/html2canvas-pro.esm.js:10840-10916),
 * and writes into the copy the computed style of every SVG and pseudo-element, read off the live page
 * (:6951-6983) — taking `.dark` off the copy later is too late for icons. So the live page is light for
 * that synchronous moment. Transitions are held, or a colour would be read halfway; the restyle back is
 * flushed before they are released, so it does not animate. Nothing is painted in between.
 */
export function inLightTheme<T>(capture: () => T): T {
  const root = document.documentElement
  if (!root.classList.contains("dark")) return capture()
  root.classList.add(CAPTURE_CLASS)
  root.classList.remove("dark")
  try {
    return capture()
  } finally {
    root.classList.add("dark")
    getComputedStyle(root).getPropertyValue("color")
    root.classList.remove(CAPTURE_CLASS)
  }
}

export function setThemePreference(preference: ThemePreference): void {
  storageService.setString(STORAGE_KEYS.THEME, preference)
  update(preference)
}

/**
 * Applies the stored choice and keeps it current: the OS switching modes (only matters in `system`)
 * and a choice made in another tab. Called once at start-up; returns the teardown.
 */
export function initTheme(): () => void {
  state = null
  update(readThemePreference())

  const onSystemChange = () => update(getThemeSnapshot().preference)
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEYS.THEME || event.key === null) update(readThemePreference())
  }
  const query = darkQuery()
  query?.addEventListener("change", onSystemChange)
  window.addEventListener("storage", onStorage)
  return () => {
    query?.removeEventListener("change", onSystemChange)
    window.removeEventListener("storage", onStorage)
  }
}
